/**
 * Admin → Channel Guides
 *
 * Draft "how to use this channel" pinned cards (title + body, EN + ES)
 * and let the bot publish them into Discord. On publish the bot:
 *   1. Posts a fresh embed
 *   2. Pins it
 *   3. Unpins + deletes the previous pinned card in that channel
 * Re-publish = clean replace. No dupes.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import {
  Pin, Plus, Send, Trash2, Pencil, CheckCircle2, AlertCircle, Rocket, Palette,
} from "lucide-react";

const TESTIDS = {
  root: "admin-channel-guides-root",
  createBtn: "admin-channel-guides-create",
  publishAllBtn: "admin-channel-guides-publish-all",
  guideRow: "admin-channel-guides-row",
  editBtn: "admin-channel-guides-edit",
  publishBtn: "admin-channel-guides-publish",
  deleteBtn: "admin-channel-guides-delete",
  dialog: "admin-channel-guides-dialog",
  saveBtn: "admin-channel-guides-save",
};

const PRESET_COLORS = [
  { name: "Amber",   value: 0xF59E0B, css: "#F59E0B" },
  { name: "Emerald", value: 0x10B981, css: "#10B981" },
  { name: "Sky",     value: 0x0EA5E9, css: "#0EA5E9" },
  { name: "Rose",    value: 0xF43F5E, css: "#F43F5E" },
  { name: "Violet",  value: 0x8B5CF6, css: "#8B5CF6" },
  { name: "Slate",   value: 0x475569, css: "#475569" },
];

export default function AdminChannelGuides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/channel-guides");
      setGuides(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load guides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async (g) => {
    setPublishing(g.id);
    try {
      const { data } = await api.post(`/admin/channel-guides/${g.id}/publish`);
      const parts = [];
      if (data.en_posted) parts.push("EN posted");
      if (data.es_posted) parts.push("ES posted");
      if (!parts.length) {
        toast.error("Publish returned no successful posts — check bot permissions");
      } else {
        toast.success(`${g.channel_name || "Guide"} — ${parts.join(" · ")}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    } finally {
      setPublishing(null);
    }
  };

  const publishAll = async () => {
    if (!window.confirm(
      `Publish ALL ${guides.length} guides to Discord?\n\nThis will unpin and delete the previous pinned card in each channel, then post a fresh one.`,
    )) return;
    setPublishingAll(true);
    try {
      const { data } = await api.post("/admin/channel-guides/publish-all");
      toast.success(`Published ${data.posted_en} EN · ${data.posted_es} ES across ${data.count} channels`);
      const failures = (data.results || []).filter((r) => !r.ok);
      failures.forEach((f) => toast.error(`${f.channel_name}: ${f.error || "failed"}`));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk publish failed");
    } finally {
      setPublishingAll(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(
      `Delete the guide for ${g.channel_name}?\n\nThe pinned message in Discord will also be unpinned and deleted.`,
    )) return;
    try {
      await api.delete(`/admin/channel-guides/${g.id}?unpin=true`);
      toast.success("Guide removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid={TESTIDS.root} className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Pin size={26} className="text-amber-600" />
            Channel Guides
          </h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            Bot-published "how to use this channel" pinned cards. Edit here → publish → the bot
            posts a fresh embed, pins it, and cleans up the previous pin. Bilingual EN + ES.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={TESTIDS.publishAllBtn}
            variant="outline"
            disabled={publishingAll || guides.length === 0}
            onClick={publishAll}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
          >
            <Rocket size={14} className="mr-1.5" />
            {publishingAll ? "Publishing…" : `Publish all (${guides.length})`}
          </Button>
          <Button
            data-testid={TESTIDS.createBtn}
            onClick={() => setEditing({})}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={14} className="mr-1.5" />
            New guide
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="text-slate-500 text-sm py-12 text-center">Loading…</div>
      ) : guides.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-16 text-center">
          <Pin size={30} className="mx-auto text-slate-300 mb-3" />
          <div className="font-semibold text-slate-700">No guides yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Create your first channel guide to auto-pin a "how to use this channel" card.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {guides.map((g) => (
            <GuideRow
              key={g.id}
              guide={g}
              publishing={publishing === g.id}
              onEdit={() => setEditing(g)}
              onPublish={() => publish(g)}
              onDelete={() => remove(g)}
            />
          ))}
        </div>
      )}

      {editing !== null && (
        <GuideEditor
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function GuideRow({ guide, publishing, onEdit, onPublish, onDelete }) {
  const publishedAt = guide.last_published_at
    ? new Date(guide.last_published_at)
    : null;
  const isPublished = !!guide.pinned_message_id_en;
  const cssColor = "#" + guide.color.toString(16).padStart(6, "0");
  return (
    <div
      data-testid={TESTIDS.guideRow}
      className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4 hover:border-slate-300 transition-colors"
    >
      <div
        className="w-1 self-stretch rounded-full"
        style={{ backgroundColor: cssColor }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold text-slate-700">
            {guide.channel_name || guide.channel_id}
          </span>
          {isPublished ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] uppercase tracking-wider">
              <CheckCircle2 size={10} className="mr-1" />
              Pinned
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px] uppercase tracking-wider">
              <AlertCircle size={10} className="mr-1" />
              Draft
            </Badge>
          )}
          {guide.channel_id_es && (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] uppercase tracking-wider">
              ES mirror
            </Badge>
          )}
        </div>
        <div className="mt-1.5 font-semibold text-slate-900 truncate">
          {guide.title_en}
        </div>
        <div className="mt-0.5 text-sm text-slate-500 line-clamp-2 whitespace-pre-line">
          {guide.body_en}
        </div>
        {publishedAt && (
          <div className="mt-1.5 text-[11px] text-slate-400 uppercase tracking-widest">
            Last published {publishedAt.toLocaleString()}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <Button
          data-testid={TESTIDS.publishBtn}
          size="sm"
          onClick={onPublish}
          disabled={publishing}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-widest text-[10px] h-8"
        >
          <Send size={12} className="mr-1" />
          {publishing ? "Posting…" : (isPublished ? "Republish" : "Publish")}
        </Button>
        <div className="flex gap-1.5">
          <Button
            data-testid={TESTIDS.editBtn}
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="h-8 flex-1"
          >
            <Pencil size={12} />
          </Button>
          <Button
            data-testid={TESTIDS.deleteBtn}
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="h-8 flex-1 text-rose-700 border-rose-200 hover:bg-rose-50"
          >
            <Trash2 size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

function GuideEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    channel_id:    initial?.channel_id || "",
    channel_name:  initial?.channel_name || "",
    title_en:      initial?.title_en || "",
    body_en:       initial?.body_en || "",
    channel_id_es: initial?.channel_id_es || "",
    title_es:      initial?.title_es || "",
    body_es:       initial?.body_es || "",
    color:         initial?.color ?? PRESET_COLORS[0].value,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.channel_id.trim()) {
      toast.error("Channel ID is required");
      return;
    }
    if (!form.title_en.trim() || !form.body_en.trim()) {
      toast.error("English title and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        channel_id_es: form.channel_id_es?.trim() || null,
        title_es:      form.title_es?.trim() || null,
        body_es:       form.body_es?.trim() || null,
      };
      if (initial?.id) {
        await api.put(`/admin/channel-guides/${initial.id}`, payload);
      } else {
        await api.post("/admin/channel-guides", payload);
      }
      toast.success(initial?.id ? "Guide updated" : "Guide created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TESTIDS.dialog}
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial?.id ? "Edit guide" : "New channel guide"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <Section title="Channel">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Channel ID (EN)" hint="Right-click channel in Discord → Copy Channel ID">
                <Input
                  data-testid="admin-channel-guides-channel-id"
                  value={form.channel_id}
                  onChange={set("channel_id")}
                  placeholder="1548058329190768760"
                  className="font-mono"
                />
              </Field>
              <Field label="Display name" hint="Just for the admin list — not sent to Discord">
                <Input
                  value={form.channel_name}
                  onChange={set("channel_name")}
                  placeholder="#the-pit"
                />
              </Field>
            </div>
          </Section>

          <Section title="English (required)">
            <Field label="Title">
              <Input
                data-testid="admin-channel-guides-title-en"
                value={form.title_en}
                onChange={set("title_en")}
                placeholder="📌 The Pit — freeform trader chat"
                maxLength={256}
              />
            </Field>
            <Field label="Body" hint="Markdown supported. Discord embed limit is 4000 chars.">
              <Textarea
                data-testid="admin-channel-guides-body-en"
                value={form.body_en}
                onChange={set("body_en")}
                rows={6}
                maxLength={3800}
                placeholder={"Freeform trader chat. Post charts, ideas, questions.\n\n**Rules**\n• Keep it on topic\n• No signals here — see #trade-setups\n• Screenshots > walls of text"}
                className="font-mono text-xs"
              />
              <div className="mt-1 text-[10px] text-slate-400 text-right tabular-nums">
                {form.body_en.length}/3800
              </div>
            </Field>
          </Section>

          <Section title="Spanish mirror (optional)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Channel ID (ES)" hint="Leave blank to skip ES mirror">
                <Input
                  value={form.channel_id_es}
                  onChange={set("channel_id_es")}
                  placeholder="1551387046323556402"
                  className="font-mono"
                />
              </Field>
              <Field label="Title (ES)">
                <Input
                  value={form.title_es}
                  onChange={set("title_es")}
                  placeholder="📌 The Pit — chat libre de traders"
                  maxLength={256}
                />
              </Field>
            </div>
            <Field label="Body (ES)">
              <Textarea
                value={form.body_es}
                onChange={set("body_es")}
                rows={5}
                maxLength={3800}
                className="font-mono text-xs"
              />
            </Field>
          </Section>

          <Section title="Accent color" icon={<Palette size={14} />}>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => set("color")(c.value)}
                  className={`h-10 px-3 rounded-lg border-2 text-xs font-bold text-white transition-all ${
                    form.color === c.value ? "ring-2 ring-offset-2 ring-slate-900" : "opacity-70 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: c.css, borderColor: c.css }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </Section>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              data-testid={TESTIDS.saveBtn}
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? "Saving…" : (initial?.id ? "Save changes" : "Create guide")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-slate-500 mb-2">
        {icon} {title}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}
